import { useEffect, useMemo, useState } from 'react';
import { StanLoader, StanModal, StanMultiSelect, StanProgressBar } from '@/components';
import { useCustomerTags } from '@/features/audience/queries/useCustomerTags';
import { useBulkCustomerActions } from '../../hooks/useBulkCustomerActions';
import type { Option } from '@/components/core-components/StanMultiSelect/types';

interface BulkTaggingModalProps {
    isOpen: boolean;
    onClose: () => void;
    mode: 'add' | 'remove';
    totalCustomers: number;
    filterArray: string | undefined;
}

export function BulkTaggingModal({ isOpen, onClose, mode, totalCustomers, filterArray }: BulkTaggingModalProps) {
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Fetch available tags (passing a dummy userFanId since we only need available tags)
    const { availableTags } = useCustomerTags(0, isOpen);

    const { batchState, isLoading, addTag, removeTag, resetState } = useBulkCustomerActions({
        filterArray,
        totalCustomers,
        onSuccess: () => {
            setSelectedTags([]);
            setError(null);
            onClose();
        },
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedTags([]);
            setError(null);
            resetState();
        }
    }, [isOpen, resetState]);

    const handleClose = () => {
        if (!isLoading) {
            setSelectedTags([]);
            setError(null);
            resetState();
            onClose();
        }
    };

    const handleSubmit = () => {
        if (selectedTags.length === 0) {
            setError('Please enter at least 1 tag!');
            return;
        }

        setError(null);
        const tag = selectedTags[0];

        if (tag) {
            if (mode === 'add') {
                addTag(tag);
            } else {
                removeTag(tag);
            }
        }
    };

    const modalTitle = useMemo(() => {
        if (isLoading) return 'Processing...';
        return mode === 'add' ? 'Add Tag' : 'Remove Tag';
    }, [isLoading, mode]);

    const modalSubheading = useMemo(() => {
        const customerText = totalCustomers === 1 ? 'customer' : 'customers';
        if (mode === 'add') {
            return `Tag will be applied to ${totalCustomers} ${customerText}`;
        }
        return `Tag will be removed from ${totalCustomers} ${customerText}`;
    }, [mode, totalCustomers]);

    const buttonText = mode === 'add' ? 'Add' : 'Remove';

    // Convert available tags to options format for StanMultiSelect
    const tagOptions = useMemo((): Option[] => {
        return availableTags.map(tag => ({ id: tag, label: tag }));
    }, [availableTags]);

    return (
        <StanModal
            id='bulk-tagging-modal'
            isOpen={isOpen}
            title={modalTitle}
            onClose={handleClose}
            primaryActionLabel={isLoading ? undefined : buttonText}
            secondaryActionLabel={isLoading ? undefined : 'Cancel'}
            primaryActionDisabled={selectedTags.length === 0}
            primaryActionLoading={isLoading}
            onPrimaryAction={handleSubmit}
            onSecondaryAction={handleClose}
            size='md'
        >
            <div className='flex flex-col gap-4'>
                <p className='text-gray text-label-sm-regular text-center'>{modalSubheading}</p>

                {isLoading ? (
                    <div className='flex flex-col items-center gap-6 py-8'>
                        <StanLoader />
                        <StanProgressBar
                            min={0}
                            max={batchState.totalCount}
                            value={batchState.processedCount}
                            className='w-full'
                        />
                        <p className='text-gray text-label-xs-regular text-center'>
                            {batchState.processedCount} / {batchState.totalCount} customers processed
                        </p>
                    </div>
                ) : (
                    <div className='flex flex-col gap-2'>
                        <StanMultiSelect
                            id='bulk-tag-select'
                            name='bulk-tag-select'
                            value={selectedTags}
                            options={tagOptions}
                            onChange={setSelectedTags}
                            placeholder='Select a tag'
                            allowCreate={mode === 'add'}
                            maxTags={1}
                        />
                        {error && <p className='text-negative text-label-xs-regular'>{error}</p>}
                    </div>
                )}
            </div>
        </StanModal>
    );
}

BulkTaggingModal.displayName = 'BulkTaggingModal';
